import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ProjectsData } from '@/data/projects-data';
import { Language, ProjectType, ProjectTechnology, ProjectTool } from '@/types';
import { ProjectTypesData } from '@/data/project-types-data';
import { useAppContext } from '@/context/AppContext';
import { useLanguageSwap } from '@/hooks/useLanguageSwap';
import styles from './ProjectDetail.module.scss';
import ImageComponent from '@/components/imageComponent/ImageComponent';
import { getIcon } from '@/utils/imageUtils';
import {
  ICON_PROJECT_DETAIL_FUNCTION_COLOR,
  ICON_PROJECT_DETAIL_USED_COLOR,
} from '@/constants/icons';
import Footer from '@/components/footer/Footer';
import SiteHeader from '@/components/siteHeader/SiteHeader';
import { createTransitionOverlay } from '@/utils/transitionOverlay';

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const project = ProjectsData.find((p) => p.id.toString() === id);
  const {
    language,
    setLanguage,
    isChangingLanguage,
    setIsChangingLanguage,
    previousLanguage,
    setPreviousLanguage,
  } = useAppContext();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [id]);

  useLanguageSwap(300);

  useEffect(() => {
    const overlay = document.getElementById('pageTransitionOverlay');
    if (overlay) {
      try {
        overlay.style.transition = 'opacity 120ms ease';
        overlay.style.opacity = '0';
        setTimeout(() => overlay.remove(), 120);
      } catch {
        overlay.remove();
      }
    }
  }, [id]);

  if (!project) {
    return (
      <div className={styles.error}>
        {language === Language.EN
          ? 'Project not found'
          : 'Projeto não encontrado'}
      </div>
    );
  }

  const currentIndex = useMemo(
    () => ProjectsData.findIndex((p) => p.id === project.id),
    [project.id],
  );

  const prevProject =
    currentIndex > 0 ? ProjectsData[currentIndex - 1] : undefined;
  const nextProject =
    currentIndex < ProjectsData.length - 1
      ? ProjectsData[currentIndex + 1]
      : undefined;

  const usedItems = useMemo(() => {
    const tools = project.tools ?? [];
    return [...project.technologies, ...tools];
  }, [project.technologies, project.tools]);

  const [tooltip, setTooltip] = useState<{
    content: React.ReactNode;
    x: number;
    y: number;
    visible: boolean;
    rich?: boolean;
  }>({ content: '', x: 0, y: 0, visible: false, rich: false });

  const showTooltip = (
    e: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>,
    content: React.ReactNode,
    rich: boolean = false,
  ) => {
    const target = e.currentTarget as HTMLDivElement;
    const rect = target.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top - 16;
    setTooltip({ content, x, y, visible: true, rich });
  };

  const hideTooltip = () => setTooltip((t) => ({ ...t, visible: false }));
  const [glossaryVisible, setGlossaryVisible] = useState(false);

  useEffect(() => {
    const hideOnScrollOrTouch = () => hideTooltip();
    window.addEventListener('scroll', hideOnScrollOrTouch, { passive: true });
    window.addEventListener('wheel', hideOnScrollOrTouch, { passive: true });
    window.addEventListener('touchmove', hideOnScrollOrTouch, {
      passive: true,
    });
    return () => {
      window.removeEventListener('scroll', hideOnScrollOrTouch);
      window.removeEventListener('wheel', hideOnScrollOrTouch);
      window.removeEventListener('touchmove', hideOnScrollOrTouch);
    };
  }, []);

  const transitionTo = (url: string) => {
    const overlay = createTransitionOverlay(project.backgroundColor, 80);
    overlay.style.opacity = '1';
    setTimeout(() => {
      navigate(url);
    }, 80);
  };

  return (
    <div
      className={styles.projectDetail}
      style={{ ['--brand-rgb' as any]: project.backgroundColor }}
    >
      {tooltip.visible && (
        <div
          className={tooltip.rich ? styles.richTooltip : styles.globalTooltip}
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          {tooltip.content}
        </div>
      )}
      <div className={styles.transitionOverlay}></div>

      <SiteHeader show={true} animated logoHref="/" theme="detail" />

      <div className={styles.mainImage}>
        <div className={styles.heroContent}>
          {project.logo ? (
            <div className={styles.projectLogo}>
              <ImageComponent
                name={project.name}
                img={project.logo}
                desc={project.name}
              />
            </div>
          ) : (
            <div className={styles.titleContainer}>
              <h1 className={styles.title}>{project.name}</h1>
            </div>
          )}

          {null}
        </div>
      </div>

      <div
        className={`${styles.content} ${isChangingLanguage ? styles.langChanging : ''}`}
      >
        {null}

        <div className={styles.aboutAndCards}>
          <div className={styles.about}>
            <p className={styles.translatable}>
              {language === Language.EN ? project.aboutEn : project.aboutPt}
            </p>
          </div>

          <div
            className={`${styles.infoCards} ${!project.client ? styles.twoCols : ''}`}
          >
            <div className={styles.infoCard}>
              <span className={`${styles.cardLabel} ${styles.translatable}`}>
                {language === Language.EN ? 'Year' : 'Ano'}
              </span>
              <span className={`${styles.cardValue} ${styles.translatable}`}>
                {project.year}
              </span>
            </div>
            <div className={styles.infoCard}>
              <span className={`${styles.cardLabel} ${styles.translatable}`}>
                {language === Language.EN ? 'Type' : 'Tipo'}
              </span>
              <span className={`${styles.cardValue} ${styles.translatable}`}>
                {language === Language.EN
                  ? ProjectTypesData[project.type].labelEn
                  : ProjectTypesData[project.type].labelPt}
              </span>
            </div>
            {project.client && (
              <div className={styles.infoCard}>
                <span className={`${styles.cardLabel} ${styles.translatable}`}>
                  {language === Language.EN ? 'Client' : 'Cliente'}
                </span>
                <span className={`${styles.cardValue} ${styles.translatable}`}>
                  {project.client}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className={styles.sectionsRow}>
          <div className={styles.sectionBlock}>
            <div
              className={styles.headerWithPopover}
              onMouseEnter={() => setGlossaryVisible(true)}
              onMouseLeave={() => setGlossaryVisible(false)}
            >
              <h3
                className={`${styles.sectionHeaderWithHelp} ${styles.translatable}`}
              >
                {language === Language.EN ? 'Functions' : 'Funções'}
                <span
                  className={styles.helpIcon}
                  tabIndex={0}
                  onFocus={() => setGlossaryVisible(true)}
                  onBlur={() => setGlossaryVisible(false)}
                  aria-label={
                    language === Language.EN
                      ? 'Functions glossary'
                      : 'Legenda das funções'
                  }
                >
                  ?
                </span>
              </h3>
              {glossaryVisible && (
                <div
                  className={styles.glossaryPopover}
                  role="dialog"
                  aria-live="polite"
                >
                  <ul className={styles.glossaryList}>
                    {project.functions.map((f) => (
                      <li key={f.name}>
                        <strong className={styles.glossaryLabel}>
                          {language === Language.EN ? f.labelEn : f.labelPt}
                        </strong>
                        :{' '}
                        {(language === Language.EN ? f.descEn : f.descPt) ||
                          f.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className={styles.iconGrid}>
              {project.functions.map((f) => (
                <div
                  key={f.name}
                  className={styles.iconItem}
                  title={f.name}
                  role="img"
                  aria-label={f.name}
                  tabIndex={0}
                  onTouchStart={(e) =>
                    showTooltip(
                      e as unknown as React.MouseEvent<HTMLElement>,
                      language === Language.EN
                        ? f.labelEn || f.name
                        : f.labelPt || f.name,
                    )
                  }
                  onTouchEnd={hideTooltip}
                  onTouchCancel={hideTooltip}
                  onMouseEnter={(e) =>
                    showTooltip(
                      e,
                      language === Language.EN
                        ? f.labelEn || f.name
                        : f.labelPt || f.name,
                    )
                  }
                  onMouseLeave={hideTooltip}
                  onFocus={(e) =>
                    showTooltip(
                      e,
                      language === Language.EN
                        ? f.labelEn || f.name
                        : f.labelPt || f.name,
                    )
                  }
                  onBlur={hideTooltip}
                >
                  <ImageComponent
                    name={f.name}
                    img={getIcon(
                      `${f.name}_ico-${ICON_PROJECT_DETAIL_FUNCTION_COLOR}.svg`,
                      'functions',
                      f.name,
                    )}
                    desc={f.name}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className={styles.sectionBlock}>
            <h3 className={styles.translatable}>
              {language === Language.EN ? 'Used' : 'Usado'}
            </h3>
            <div className={styles.iconGrid}>
              {usedItems.map((item) => {
                const techValues = Object.values(ProjectTechnology) as string[];
                const isTechnology = techValues.includes(item.name as string);
                const category = isTechnology ? 'technologies' : 'tools';
                const iconSrc = getIcon(
                  `${item.name}_ico-${ICON_PROJECT_DETAIL_USED_COLOR}.svg`,
                  category,
                  item.name,
                );
                return (
                  <div
                    key={item.name}
                    className={styles.iconItem}
                    title={item.name}
                    role="img"
                    aria-label={item.name}
                    tabIndex={0}
                    onTouchStart={(e) =>
                      showTooltip(
                        e as unknown as React.MouseEvent<HTMLElement>,
                        item.name,
                      )
                    }
                    onTouchEnd={hideTooltip}
                    onTouchCancel={hideTooltip}
                    onMouseEnter={(e) => showTooltip(e, item.name)}
                    onMouseLeave={hideTooltip}
                    onFocus={(e) => showTooltip(e, item.name)}
                    onBlur={hideTooltip}
                  >
                    <ImageComponent
                      name={item.name}
                      img={iconSrc}
                      desc={item.name}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Video or Live Demo */}
        <div className={styles.demoSection}>
          {project.type === ProjectType.ACADEMICAL && project.url ? (
            <a
              className={styles.demoLink}
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {language === Language.EN ? 'DEMO' : 'DEMO'}
            </a>
          ) : project.video ? (
            <div className={styles.videoContainer}>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video controls preload="metadata" className={styles.video}>
                <source src={project.video} />
              </video>
            </div>
          ) : null}
        </div>
      </div>

      <div className={styles.navigation}>
        {prevProject ? (
          <Link
            className={styles.navItem}
            to={`/project/${prevProject.id}`}
            onClick={(e) => {
              e.preventDefault();
              transitionTo(`/project/${prevProject.id}`);
            }}
          >
            <span className={styles.arrow}>⟵</span>{' '}
            {language === Language.EN ? 'Previous' : 'Anterior'}
          </Link>
        ) : (
          <div className={styles.navItemPlaceholder} />
        )}

        <Link
          className={styles.backButton}
          to="/#projects"
          onClick={(e) => {
            e.preventDefault();
            transitionTo('/#projects');
          }}
        >
          {language === Language.EN ? 'Back' : 'Voltar'}
        </Link>

        {nextProject ? (
          <Link
            className={styles.navItem}
            to={`/project/${nextProject.id}`}
            onClick={(e) => {
              e.preventDefault();
              transitionTo(`/project/${nextProject.id}`);
            }}
          >
            {language === Language.EN ? 'Next' : 'Seguinte'}{' '}
            <span className={styles.arrow}>⟶</span>
          </Link>
        ) : (
          <div className={styles.navItemPlaceholder} />
        )}
      </div>
      <Footer />
    </div>
  );
};

ProjectDetail.displayName = 'ProjectDetail';
export default ProjectDetail;
